
import Vue from 'vue';
import { Moment } from 'moment';
import { Component, Prop, Watch } from 'vue-property-decorator';
import OrderedQueue from '@/types/OrderedQueue';
import { QueueEntry } from '@/types/QueueEntry';
import ErrorDialog from '@/util/ErrorDialog';
import EscapeHTML from '@/util/Sanitization';
import * as PromptHandler from '@/util/PromptHandler';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
	faUser,
	faQuestion,
	faLink,
	faUserPlus,
	faCheck,
	faEdit,
	faMapMarker,
} from '@fortawesome/free-solid-svg-icons';

library.add(
	faUser,
	faQuestion,
	faLink,
	faUserPlus,
	faCheck,
	faEdit,
	faMapMarker
);

@Component
export default class QueueSignup extends Vue {
	description = '';
	location = '';
	customResponses: string[] = [];

	// Character limits matching server-side validation
	readonly maxDescriptionLength = 1500;
	readonly maxLocationLength = 300;

	@Prop({ required: true }) queue!: OrderedQueue;
	@Prop({ required: true }) time!: Moment;

	private hasDescWithPrompts(): boolean {
		return PromptHandler.hasCustomPrompts(this.queue.config);
	}

	private descWithPromptsToDescription(): string {
		return PromptHandler.responsesToDescription(this.customResponses);
	}

	private descriptionToDescWithPrompts(description: string): string[] {
		return PromptHandler.descriptionToResponses(description);
	}

	@Watch('myEntry')
	myEntryUpdated(newEntry: QueueEntry | null) {
		if (newEntry !== null) {
			if (this.hasDescWithPrompts()) {
				this.customResponses = this.descriptionToDescWithPrompts(
					newEntry.description || ''
				);
			} else {
				this.description = newEntry.description || '';
			}
			this.location = newEntry.location || '';
		}
	}

	get totalCustomResponseLength(): number {
		if (!this.customResponses.length) return 0;
		return this.descWithPromptsToDescription().length;
	}

	get isDescriptionTooLong(): boolean {
		return this.hasDescWithPrompts()
			? this.totalCustomResponseLength > this.maxDescriptionLength
			: this.description.length > this.maxDescriptionLength;
	}

	get isLocationTooLong(): boolean {
		return this.location.length > this.maxLocationLength;
	}

	get canSignUp(): boolean {
		// Do not change the order of the expressions in this boolean
		// expression. Because myEntry is a computed property, it seems
		// that it has to be calculated at least once in order to be
		// reactive, which means that putting it at the end of the
		// expression means it isn't calculated until all of the previous
		// parts of the expression are true, which is only calculated when
		// deemed necessary based on reactivity. Thus, if one of the previous
		// parts of the expression return false on the first calculation,
		// we aren't reactive on myEntry until one of the previous
		// parts had a reactive update. This took way too long to figure out :(

		const isValidDescription = this.hasDescWithPrompts()
			? PromptHandler.areResponsesValid(
					this.customResponses,
					this.queue.config!.prompts!
			  )
			: this.description.trim() !== '';

		return (
			this.myEntry === null &&
			this.$root.$data.loggedIn &&
			this.queue.isOpen(this.time) &&
			isValidDescription &&
			(this.location.trim() !== '' ||
				!this.queue.config?.enableLocationField) &&
			!this.isDescriptionTooLong &&
			!this.isLocationTooLong
		);
	}

	get myEntryIndex(): number {
		return this.queue.entryIndex(this.$root.$data.userInfo.email);
	}

	get myEntry(): QueueEntry | null {
		return this.queue.entry(this.$root.$data.userInfo.email);
	}

	get myEntryModified() {
		const e = this.myEntry;
		if (e === null) return false;

		if (this.hasDescWithPrompts()) {
			try {
				const parsedDescription = JSON.parse(e.description || '{}');
				return (
					JSON.stringify(parsedDescription) !==
						this.descWithPromptsToDescription() || e.location !== this.location
				);
			} catch (e) {
				// If description is not valid JSON, consider it modified
				return true;
			}
		}

		return e.description !== this.description || e.location !== this.location;
	}

	signUp() {
		if (this.queue.config?.confirmSignupMessage !== undefined) {
			return this.$buefy.dialog.confirm({
				title: 'Sign Up',
				message: EscapeHTML(this.queue.config!.confirmSignupMessage),
				type: 'is-warning',
				hasIcon: true,
				onConfirm: this.signUpRequest,
			});
		}

		this.signUpRequest();
	}

	signUpRequest() {
		// No, this doesn't prevent students from manually hitting the API to specify
		// a location. l33t h4x!
		const location = this.queue.config?.enableLocationField
			? this.location
			: '(disabled)';

		const description = this.hasDescWithPrompts()
			? this.descWithPromptsToDescription()
			: this.description;

		fetch(process.env.BASE_URL + `api/queues/${this.queue.id}/entries`, {
			method: 'POST',
			body: JSON.stringify({
				description,
				location,
			}),
		}).then((res) => {
			if (res.status !== 201) {
				return ErrorDialog(res);
			}

			this.$buefy.toast.open({
				duration: 5000,
				message: `You're on the queue, ${EscapeHTML(
					this.$root.$data.userInfo.first_name
				)}!`,
				type: 'is-success',
			});
		});
	}

	updateRequest() {
		const description = this.hasDescWithPrompts()
			? this.descWithPromptsToDescription()
			: this.description;

		if (this.myEntry !== null) {
			fetch(
				process.env.BASE_URL +
					`api/queues/${this.queue.id}/entries/${this.myEntry.id}`,
				{
					method: 'PUT',
					body: JSON.stringify({
						description,
						location: this.location,
					}),
				}
			).then((res) => {
				if (res.status !== 204) {
					return ErrorDialog(res);
				}

				this.$buefy.toast.open({
					duration: 5000,
					message: 'Your request has been updated!',
					type: 'is-success',
				});
			});
		}
	}
}
